/**
 * NewPickingSlipModal — Iter 382
 *
 * Three-tab wizard for creating a new picking slip:
 *   • Manual        — pick a customer (+ optional project) → empty draft
 *   • From existing sales order — pick a customer, list their SOs, click one
 *   • Drag & drop   — upload a SO file, backend runs OCR + dedupe + generate
 *
 * When triggered from the project's Picking card, `projectId` is pinned so
 * the manual tab hides the customer picker.
 */
import { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { X, Loader2, FileText, Upload, Package, ChevronRight, CheckCircle2, Search } from "lucide-react";
import { toast } from "sonner";
import api, { formatErr } from "../../lib/api";

// Iter 410.3 — Searchable customer picker. Replaces the raw <select>
// that got unusable once tenants passed ~40 customers. Type-to-filter
// with an inline dropdown. Selection commits the id + name.
function CustomerCombobox({ value, onChange, customers, testid }) {
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const boxRef = useRef(null);

  const selected = customers.find((c) => c.id === value);
  const term = q.trim().toLowerCase();
  const shown = term
    ? customers
        .filter((c) => (c.name || "").toLowerCase().includes(term))
        .slice(0, 30)
    : customers.slice(0, 30);

  useEffect(() => {
    const onDoc = (e) => {
      if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  return (
    <div className="relative" ref={boxRef}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="hv-input w-full text-sm text-left flex items-center gap-2"
        data-testid={testid}
      >
        <Search className="w-3 h-3 text-[#647087] shrink-0" />
        <span className={`flex-1 truncate ${selected ? "text-[#dde3ec]" : "text-[#647087]"}`}>
          {selected?.name || "— pick a customer —"}
        </span>
      </button>
      {open && (
        <div className="absolute left-0 right-0 top-full mt-1 z-30 hv-card p-1.5 space-y-1"
             data-testid={`${testid}-menu`}>
          <input
            autoFocus
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search customers…"
            className="hv-input w-full text-xs py-1"
            data-testid={`${testid}-input`}
          />
          <div className="max-h-64 overflow-y-auto space-y-0.5">
            {shown.length === 0 ? (
              <div className="text-[10px] text-[#647087] p-2 text-center">
                {term ? "No matches." : "No customers loaded yet."}
              </div>
            ) : shown.map((c) => (
              <button
                key={c.id}
                type="button"
                onClick={() => { onChange(c.id); setOpen(false); setQ(""); }}
                className={`w-full text-left px-2 py-1 rounded text-xs hover:bg-white/5 ${
                  c.id === value ? "bg-cyan-500/10 text-cyan-300" : "text-[#dde3ec]"
                }`}
                data-testid={`${testid}-opt-${c.id}`}
              >
                {c.name}
              </button>
            ))}
            {!term && customers.length > 30 && (
              <div className="text-[9px] text-[#647087] px-2 py-1">
                Showing 30 of {customers.length} — type to filter.
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

const TABS = [
  { key: "manual",  label: "Manual",       icon: Package,   testid: "new-picking-tab-manual" },
  { key: "so",      label: "From sales order", icon: FileText, testid: "new-picking-tab-so" },
  { key: "scan",    label: "Drag & drop SO", icon: Upload,   testid: "new-picking-tab-scan" },
];

export default function NewPickingSlipModal({
  open,
  onClose,
  onCreated,
  projectId = null,     // when opened from ProjectPickingCard
  customerId = null,    // pre-pinned customer (optional)
}) {
  const [tab, setTab] = useState("so");
  const [busy, setBusy] = useState(false);

  if (!open) return null;

  const handleCreated = (payload) => {
    onCreated?.(payload);
    onClose?.();
  };

  return (
    <div
      className="fixed inset-0 z-[80] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
      onClick={(e) => e.target === e.currentTarget && onClose?.()}
      data-testid="new-picking-modal"
    >
      <div className="hv-card w-full max-w-lg p-4 space-y-3">
        <div className="flex items-center justify-between gap-2">
          <div>
            <div className="text-xs uppercase tracking-[0.2em] text-[#647087]">Create picking slip</div>
            <div className="text-sm text-white mt-0.5">Choose how to start</div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-1 rounded hover:bg-white/5 text-[#a0aabf]"
            data-testid="new-picking-close"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="flex items-center gap-1 border-b border-white/5">
          {TABS.map((t) => (
            <button
              key={t.key}
              type="button"
              onClick={() => setTab(t.key)}
              className={`px-3 py-2 text-xs flex items-center gap-1.5 border-b-2 transition ${
                tab === t.key
                  ? "border-[#1FB6C1] text-white"
                  : "border-transparent text-[#647087] hover:text-white"
              }`}
              data-testid={t.testid}
            >
              <t.icon className="w-3 h-3" /> {t.label}
            </button>
          ))}
        </div>

        {tab === "manual" && (
          <ManualTab
            projectId={projectId}
            customerId={customerId}
            busy={busy}
            setBusy={setBusy}
            onCreated={handleCreated}
          />
        )}
        {tab === "so" && (
          <FromSOTab
            projectId={projectId}
            customerId={customerId}
            busy={busy}
            setBusy={setBusy}
            onCreated={handleCreated}
          />
        )}
        {tab === "scan" && (
          <ScanTab
            projectId={projectId}
            customerId={customerId}
            busy={busy}
            setBusy={setBusy}
            onCreated={handleCreated}
          />
        )}
      </div>
    </div>
  );
}

// ───────── Manual ─────────

function ManualTab({ projectId, customerId, busy, setBusy, onCreated }) {
  const [name, setName] = useState("");
  const [cust, setCust] = useState(customerId || "");
  const [customers, setCustomers] = useState([]);

  useEffect(() => {
    if (projectId || customerId) return;
    api.get("/customers", { params: { limit: 500 } })
      .then((r) => setCustomers(Array.isArray(r.data) ? r.data : (r.data?.items || [])))
      .catch(() => {});
  }, [projectId, customerId]);

  const create = async () => {
    if (!projectId && !cust) {
      toast.error("Pick a customer first.");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/picking/lists", {
        project_id: projectId || null,
        customer_id: cust || null,
        name: name || null,
      });
      toast.success("Picking list created.");
      onCreated({ list: data });
    } catch (e) {
      toast.error(formatErr(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-3">
      <div className="text-[11px] text-[#647087]">
        Create an empty picking list. Add materials manually.
      </div>
      {!projectId && !customerId && (
        <label className="block text-xs">
          <div className="text-[10px] uppercase tracking-wider text-[#647087] mb-1">Customer</div>
          <CustomerCombobox
            value={cust}
            onChange={setCust}
            customers={customers}
            testid="new-picking-manual-customer"
          />
        </label>
      )}
      <label className="block text-xs">
        <div className="text-[10px] uppercase tracking-wider text-[#647087] mb-1">
          Name <span className="text-[#647087]">(optional)</span>
        </div>
        <input
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder={projectId ? `Picking · ${new Date().toLocaleDateString("en-GB")}` : "Picking — ad-hoc"}
          className="hv-input w-full text-sm"
          data-testid="new-picking-manual-name"
        />
      </label>
      <div className="flex items-center justify-end gap-2 pt-1">
        <button
          type="button"
          onClick={create}
          disabled={busy}
          className="hv-btn hv-btn-primary text-xs disabled:opacity-50"
          data-testid="new-picking-manual-create"
        >
          {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Package className="w-3 h-3" />}
          {busy ? "Creating…" : "Create empty list"}
        </button>
      </div>
    </div>
  );
}

// ───────── From existing Sales Order ─────────

function FromSOTab({ projectId, customerId, busy, setBusy, onCreated }) {
  const [cust, setCust] = useState(customerId || "");
  const [customers, setCustomers] = useState([]);
  const [orders, setOrders] = useState([]);
  const [loadingOrders, setLoadingOrders] = useState(false);
  const [q, setQ] = useState("");
  // Iter 382 — when opened from a project, the SO list is already
  // project-scoped, so the customer picker is redundant noise.
  const showCustomerPicker = !customerId && !projectId;

  useEffect(() => {
    if (customerId || projectId) return;
    api.get("/customers", { params: { limit: 500 } })
      .then((r) => setCustomers(Array.isArray(r.data) ? r.data : (r.data?.items || [])))
      .catch(() => {});
  }, [customerId, projectId]);

  const loadOrders = useCallback(async () => {
    if (!cust && !projectId) { setOrders([]); return; }
    setLoadingOrders(true);
    try {
      const params = { category: "sales_order", limit: 200 };
      if (cust) params.customer_id = cust;
      if (projectId) params.project_id = projectId;
      const { data } = await api.get("/sales-documents", { params });
      setOrders(Array.isArray(data) ? data : (data?.items || []));
    } catch {
      setOrders([]);
    } finally {
      setLoadingOrders(false);
    }
  }, [cust, projectId]);

  useEffect(() => { loadOrders(); }, [loadOrders]);

  const pickSO = async (so) => {
    setBusy(true);
    try {
      const { data } = await api.post("/picking/from-sales-order", {
        sales_doc_id: so.id,
      });
      const linkedTxt = data.jobcards_linked?.length
        ? ` · Linked to ${data.jobcards_linked.length} job card${data.jobcards_linked.length === 1 ? "" : "s"}`
        : "";
      toast.success(`Picking created from SO ${data.sales_doc_number || so.id.slice(0, 6)} · ${data.items_created} rows${linkedTxt}`);
      onCreated(data);
    } catch (e) {
      toast.error(formatErr(e));
    } finally {
      setBusy(false);
    }
  };

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return orders;
    return orders.filter((o) =>
      [o.document_number, o.title, o.short_id]
        .filter(Boolean)
        .some((v) => v.toString().toLowerCase().includes(s)),
    );
  }, [orders, q]);

  return (
    <div className="space-y-3">
      <div className="text-[11px] text-[#647087]">
        Pick a sales order that&apos;s already in the system. Materials copy across; job cards on the same SO auto-link.
      </div>
      {showCustomerPicker && (
        <label className="block text-xs">
          <div className="text-[10px] uppercase tracking-wider text-[#647087] mb-1">Customer</div>
          <CustomerCombobox
            value={cust}
            onChange={setCust}
            customers={customers}
            testid="new-picking-so-customer"
          />
        </label>
      )}
      {(cust || projectId) && (
        <>
          <input
            type="search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search by SO number or title…"
            className="hv-input w-full text-sm"
            data-testid="new-picking-so-search"
          />
          <div className="max-h-64 overflow-y-auto border border-white/5 rounded" data-testid="new-picking-so-list">
            {loadingOrders && <div className="p-3 text-xs text-[#647087]"><Loader2 className="w-3 h-3 inline animate-spin" /> Loading sales orders…</div>}
            {!loadingOrders && filtered.length === 0 && (
              <div className="p-3 text-xs text-[#647087]">
                No sales orders on this customer yet. Drop a file in the next tab.
              </div>
            )}
            {!loadingOrders && filtered.map((so) => (
              <button
                key={so.id}
                type="button"
                onClick={() => pickSO(so)}
                disabled={busy}
                className="w-full text-left px-3 py-2 text-xs border-b border-white/5 hover:bg-white/5 flex items-center gap-2 disabled:opacity-50"
                data-testid={`new-picking-so-pick-${so.id}`}
              >
                <FileText className="w-3.5 h-3.5 text-[#1FB6C1] shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="text-white font-medium truncate">
                    {so.document_number || so.title || `SO ${so.id.slice(0, 8)}`}
                  </div>
                  <div className="text-[10px] text-[#647087] truncate">
                    {so.document_date || "—"} · {so.status || "open"}
                    {so.line_items?.length ? ` · ${so.line_items.length} lines` : ""}
                  </div>
                </div>
                <ChevronRight className="w-3 h-3 text-[#647087] shrink-0" />
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

// ───────── Drag & drop SO scan ─────────

function ScanTab({ projectId, customerId: initialCust, busy, setBusy, onCreated }) {
  const [customers, setCustomers] = useState([]);
  const [cust, setCust] = useState(initialCust || "");
  const [file, setFile] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (initialCust) return;
    api.get("/customers", { params: { limit: 500 } })
      .then((r) => setCustomers(Array.isArray(r.data) ? r.data : (r.data?.items || [])))
      .catch(() => {});
  }, [initialCust]);

  const submit = async () => {
    if (!file) { toast.error("Drop a file first."); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      if (cust) fd.append("customer_id", cust);
      const { data } = await api.post("/picking/from-so-scan", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 90_000,
      });
      const path = { hash: "existing SO matched by file hash", doc_number: "existing SO matched by number", new: "new SO created from scan" };
      const linkedTxt = data.jobcards_linked?.length
        ? ` · Linked to ${data.jobcards_linked.length} job card${data.jobcards_linked.length === 1 ? "" : "s"}`
        : "";
      toast.success(`Picking created · ${data.items_created} rows · ${path[data.dedup_path]}${linkedTxt}`);
      onCreated(data);
    } catch (e) {
      toast.error(formatErr(e));
    } finally {
      setBusy(false);
    }
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const f = e.dataTransfer.files?.[0];
    if (f) setFile(f);
  };

  return (
    <div className="space-y-3">
      <div className="text-[11px] text-[#647087]">
        Drop a sales-order PDF/image. OCR extracts line items, dedupes against existing docs (hash first, then doc number),
        and auto-links to job cards already tied to that SO.
      </div>
      {!initialCust && (
        <label className="block text-xs">
          <div className="text-[10px] uppercase tracking-wider text-[#647087] mb-1">Customer (required for new SOs)</div>
          <CustomerCombobox
            value={cust}
            onChange={setCust}
            customers={customers}
            testid="new-picking-scan-customer"
          />
        </label>
      )}

      <div
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={onDrop}
        onClick={() => fileInputRef.current?.click()}
        className={`p-6 border-2 border-dashed rounded text-center cursor-pointer transition ${
          dragOver ? "border-[#1FB6C1] bg-[#1FB6C1]/10" : "border-white/10 hover:border-white/20"
        }`}
        data-testid="new-picking-scan-drop"
      >
        <input
          ref={fileInputRef}
          type="file"
          accept="application/pdf,image/*"
          className="hidden"
          onChange={(e) => setFile(e.target.files?.[0] || null)}
          data-testid="new-picking-scan-file"
        />
        {file ? (
          <div className="flex flex-col items-center gap-1">
            <CheckCircle2 className="w-6 h-6 text-[#10B981]" />
            <div className="text-xs text-white font-medium">{file.name}</div>
            <div className="text-[10px] text-[#647087]">{(file.size / 1024).toFixed(1)} KB · click to change</div>
          </div>
        ) : (
          <div className="flex flex-col items-center gap-1 text-[#647087]">
            <Upload className="w-6 h-6" />
            <div className="text-xs">Drag & drop or click to upload</div>
            <div className="text-[10px]">PDF or image · max 20 MB</div>
          </div>
        )}
      </div>

      <div className="flex items-center justify-end gap-2 pt-1">
        <button
          type="button"
          onClick={submit}
          disabled={busy || !file}
          className="hv-btn hv-btn-primary text-xs disabled:opacity-50"
          data-testid="new-picking-scan-create"
        >
          {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Upload className="w-3 h-3" />}
          {busy ? "Scanning…" : "OCR & generate"}
        </button>
      </div>
    </div>
  );
}
